/**
 * WidgetGrid — Monday-style dashboard widgets (Phase 5, 2026-05-16).
 *
 * 12-column responsive grid. User może:
 *   - Drag widgety (zmień kolejność)
 *   - Resize w/h (3 handles: SE corner)
 *   - Dodać nowy / usunąć
 *
 * Bez zewnętrznych libów — używamy CSS grid + native HTML5 drag/drop.
 * Każdy widget renderuje się przez WidgetRenderer (per widget_type).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import {
  Plus, X as XIcon, GripVertical, RotateCcw, Loader2, Maximize2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listDashboardWidgets, addDashboardWidget, updateDashboardWidget,
  deleteDashboardWidget, resetDashboardWidgets, getWidgetData,
} from '../../lib/api';
import { WidgetRenderer, WIDGET_TYPES } from './widgetRenderers';


const GRID_COLS = 12;
const ROW_HEIGHT = 60; // px


function WidgetCard({ widget, onRemove, editing, onResize, onDragStart, onDragOver, onDrop, dragging }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = widget.config || {};
      const d = await getWidgetData(widget.widget_type, cfg);
      setData(d);
      setError(null);
    } catch (err) {
      setError('Błąd ładowania');
    } finally {
      setLoading(false);
    }
  }, [widget.widget_type, widget.config]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh data co 30s (tylko gdy widget jest visible)
  useEffect(() => {
    const id = setInterval(loadData, 30000);
    return () => clearInterval(id);
  }, [loadData]);

  const pos = widget.position || { x: 0, y: 0, w: 4, h: 3 };
  const style = {
    gridColumn: `${pos.x + 1} / span ${pos.w}`,
    gridRow: `${pos.y + 1} / span ${pos.h}`,
    minHeight: pos.h * ROW_HEIGHT,
    opacity: dragging ? 0.4 : 1,
  };

  return (
    <Card
      style={style}
      className="overflow-hidden flex flex-col"
      data-testid={`widget-${widget.widget_type}`}
      draggable={editing}
      onDragStart={(e) => editing && onDragStart(e, widget)}
      onDragOver={(e) => editing && onDragOver(e, widget)}
      onDrop={(e) => editing && onDrop(e, widget)}
    >
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-muted/30">
        <div className="flex items-center gap-1 min-w-0">
          {editing && <GripVertical className="w-3 h-3 text-muted-foreground shrink-0 cursor-grab" />}
          <span className="text-xs font-medium truncate">
            {widget.title || WIDGET_TYPES[widget.widget_type]?.label || widget.widget_type}
          </span>
        </div>
        {editing && (
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={() => onResize(widget, +1, 0)}
              title="Szerszy"
            >+</Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={() => onResize(widget, -1, 0)}
              title="Węższy"
            >−</Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={() => onResize(widget, 0, +1)}
              title="Wyższy"
            >↓</Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={() => onResize(widget, 0, -1)}
              title="Niższy"
            >↑</Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 text-red-300 hover:text-red-200"
              onClick={() => onRemove(widget)}
            >
              <XIcon className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto p-2">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : error ? (
          <div className="text-xs text-red-300 text-center py-4">{error}</div>
        ) : (
          <WidgetRenderer widget={widget} data={data} />
        )}
      </div>
    </Card>
  );
}


function AddWidgetDialog({ open, onClose, onAdd }) {
  const [type, setType] = useState('kpi_stage_counts');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    setSaving(true);
    try {
      const meta = WIDGET_TYPES[type] || {};
      await onAdd({
        widget_type: type,
        title: title.trim() || null,
        position: meta.default_position || { x: 0, y: 99, w: 6, h: 3 },
        config: meta.default_config || {},
      });
      onClose();
      setTitle('');
    } catch { toast.error('Nie udało się dodać'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Dodaj widget</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-muted-foreground">Typ widgetu</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(WIDGET_TYPES).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Tytuł (opcjonalnie)</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={WIDGET_TYPES[type]?.label}
              className="h-8 text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={handleAdd} disabled={saving}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            Dodaj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function WidgetGrid() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [dragWidget, setDragWidget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDashboardWidgets();
      setWidgets(r.items || []);
    } catch (err) {
      toast.error('Nie udało się załadować dashboardu');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (body) => {
    await addDashboardWidget(body);
    await load();
  };

  const handleRemove = async (w) => {
    if (!window.confirm(`Usunąć widget "${w.title || w.widget_type}"?`)) return;
    await deleteDashboardWidget(w.id);
    toast.success('Usunięto');
    await load();
  };

  const handleResize = async (w, dw, dh) => {
    const pos = w.position || { x: 0, y: 0, w: 4, h: 3 };
    const newW = Math.max(2, Math.min(GRID_COLS, pos.w + dw));
    const newH = Math.max(2, Math.min(12, pos.h + dh));
    if (newW === pos.w && newH === pos.h) return;
    // Optimistic
    setWidgets(prev => prev.map(x =>
      x.id === w.id ? { ...x, position: { ...pos, w: newW, h: newH } } : x
    ));
    try {
      await updateDashboardWidget(w.id, { position: { ...pos, w: newW, h: newH } });
    } catch { load(); }
  };

  const handleDragStart = (e, w) => { setDragWidget(w); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = async (e, targetWidget) => {
    e.preventDefault();
    if (!dragWidget || dragWidget.id === targetWidget.id) { setDragWidget(null); return; }
    // Swap positions
    const srcPos = { ...dragWidget.position };
    const dstPos = { ...targetWidget.position };
    setWidgets(prev => prev.map(x => {
      if (x.id === dragWidget.id) return { ...x, position: dstPos };
      if (x.id === targetWidget.id) return { ...x, position: srcPos };
      return x;
    }));
    try {
      await Promise.all([
        updateDashboardWidget(dragWidget.id, { position: dstPos }),
        updateDashboardWidget(targetWidget.id, { position: srcPos }),
      ]);
    } catch { load(); }
    setDragWidget(null);
  };

  const handleReset = async () => {
    if (!window.confirm('Przywrócić domyślną kompozycję widgetów? Twoje obecne ustawienia zostaną usunięte.')) return;
    try {
      await resetDashboardWidgets();
      toast.success('Przywrócono domyślne');
      await load();
    } catch { toast.error('Reset failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Moje widgety</h2>
          <Badge variant="outline" className="text-[10px]">{widgets.length}</Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant={editing ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEditing(!editing)}
            className="h-8 gap-1"
            data-testid="toggle-edit-dashboard"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            {editing ? 'Zakończ edycję' : 'Edytuj'}
          </Button>
          {editing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="h-8 gap-1">
                <Plus className="w-3.5 h-3.5" />
                Dodaj widget
              </Button>
              <Button variant="outline" size="sm" onClick={handleReset} className="h-8 gap-1">
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-12 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 col-span-6" />)}
        </div>
      ) : (
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
            gridAutoRows: `${ROW_HEIGHT}px`,
            gridAutoFlow: 'dense',
          }}
          data-testid="widget-grid"
        >
          {widgets.map(w => (
            <WidgetCard
              key={w.id}
              widget={w}
              editing={editing}
              onRemove={handleRemove}
              onResize={handleResize}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              dragging={dragWidget?.id === w.id}
            />
          ))}
        </div>
      )}

      <AddWidgetDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
      />
    </div>
  );
}
